/**
 * Feature: Canvassing & Instant Quote Embed Widget
 * /features/canvassing-widget
 *
 * SoftwareApplication + FAQ schema. Frictionless lead capture focus.
 */
import {
  FeatureFaq,
  FeatureSignup,
  FeatureHeroBadge,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import {
  Globe,
  Calculator,
  Clock,
  Code2,
  LayoutDashboard,
  ArrowRight,
  CheckCircle2,
  Smartphone,
  Footprints,
} from 'lucide-react';
import { ProductShowcase } from '@/components/marketing/ProductShowcase';
import { PRODUCT_FRAMES } from '@/lib/productFrames';

const faqs = [
  {
    question: 'How does the embed widget work on my website?',
    answer:
      'You add a single line of embed code to your website. Homeowners enter their address, and our satellite-powered engine instantly generates a roof measurement and quote — no page reload, no redirects. The lead data flows directly into your Rooflyzer dashboard.',
  },
  {
    question: 'Does the widget slow down my website?',
    answer:
      "No. The widget loads asynchronously and is under 50 KB. It won't affect your page speed score, which is critical for SEO rankings.",
  },
  {
    question: 'Can my canvassers use the estimator in the field?',
    answer:
      'Yes. The mobile-optimized estimator lets canvassers generate quotes on the spot, adding GPS coordinates and prospect notes that sync to the CRM instantly.',
  },
  {
    question: 'Do I need a developer to install the widget?',
    answer:
      'No. If you can paste a line of code into your website (like a Google Analytics snippet), you can install the widget. We also provide WordPress and Wix one-click install guides.',
  },
];

export const CanvassingWidgetPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Instant Quote Widget & Canvassing Tools',
    description:
      'Turn your website into a 24/7 salesperson with a proprietary embed widget that generates instant roof quotes. Integrate canvassing results directly into your CRM pipeline.',
    featureList: [
      'Website embed quote widget',
      'Satellite-powered instant estimates',
      'GPS canvassing tracker',
      'Mobile-optimized field tools',
      'Lead auto-capture to CRM',
      'Multi-tier pricing display',
    ],
    url: 'https://rooflyzer.pro/features/canvassing-widget',
    offers: { price: '0', description: 'Free tier — unlimited estimates' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Door-to-Door Canvassing App for Roofers"
        description="Map-based canvassing tools for roofing sales teams. Track knockers, manage territories, and capture leads from the field."
        canonicalUrl="/features/canvassing-widget"
        ogImage="/og/canvassing.png"
        keywords="roofing canvassing app, door to door roofing, roofing territory management, roofing field sales, roofing embed widget"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Canvassing & Widget', url: '/features/canvassing-widget' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={Globe}>24/7 Lead Capture</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Your Website.{' '}
              <span className="text-white underline decoration-slate-600 decoration-2 underline-offset-8">
                Your Best Salesperson.
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Turn your website into a 24/7 salesperson. Our proprietary embed widget allows
              homeowners to generate instant quotes while your canvassers are still on the street.
              It's frictionless, professional, and integrates directly into your dashboard—no
              third-party "glue" code required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm px-8"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Get Your Embed Widget
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Button
                size="lg"
                variant="ghost"
                className="border border-white/20 text-white hover:bg-white/10"
                onClick={() => window.location.assign('/estimator')}
              >
                Try Live Demo
              </Button>
            </div>
          </section>

          {/* Product film */}
          <section className="mb-24">
            <ProductShowcase frame={PRODUCT_FRAMES.instant} float={false} />
          </section>

          {/* Two Pillars: Website Widget + Canvassing */}
          <section className="mb-24">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Column 1: Website Widget */}
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
                <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-6">
                  <Code2 className="h-7 w-7 text-slate-300" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Instant Quote Embed Widget</h2>
                <p className="text-white/80 mb-6 leading-relaxed">
                  One line of code. Homeowners enter their address, get an instant satellite-powered
                  roof measurement and pricing options. The lead flows into your dashboard
                  automatically.
                </p>
                <ul className="space-y-3">
                  {[
                    'Satellite-powered instant measurements',
                    'Multi-tier pricing (Economy / Standard / Premium)',
                    'Lead auto-capture with source attribution',
                    'Mobile responsive — works on any device',
                    'Custom branding with your logo & colors',
                    'No page reload or redirects',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                      <CheckCircle2 className="size-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Column 2: Canvassing Tools */}
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
                <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-6">
                  <Footprints className="h-7 w-7 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Field Canvassing Suite</h2>
                <p className="text-white/80 mb-6 leading-relaxed">
                  Your door knockers generate instant estimates on the homeowner's doorstep. GPS
                  tracked, outcome recorded, lead created — all from one mobile-friendly interface.
                </p>
                <ul className="space-y-3">
                  {[
                    'GPS coordinate tracking for every knock',
                    'Instant on-site quote generation',
                    'Outcome recording (interested / not home / DNC)',
                    'Territory heat maps for coverage planning',
                    'Photo capture with location stamp',
                    'Syncs to pipeline in real-time',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                      <CheckCircle2 className="size-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              How It Works
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  step: '1',
                  icon: Code2,
                  title: 'Paste the Embed',
                  desc: 'Add one line of code to your site. Works with WordPress, Wix, Squarespace, and custom HTML.',
                },
                {
                  step: '2',
                  icon: Smartphone,
                  title: 'Customer Enters Address',
                  desc: 'Homeowner types their address. Our satellite engine measures the roof instantly.',
                },
                {
                  step: '3',
                  icon: Calculator,
                  title: 'Instant Quote Generated',
                  desc: 'Multi-tier pricing displayed in seconds. Branded with your company details.',
                },
                {
                  step: '4',
                  icon: LayoutDashboard,
                  title: 'Lead Hits Your Dashboard',
                  desc: 'Contact info, property data, and quote are in your CRM. Follow up immediately.',
                },
              ].map((item, i) => (
                <div key={i} className="relative">
                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10 h-full">
                    <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center mb-4 text-white font-mono font-bold">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-white/70 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Stats */}
          <section className="mb-24">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { value: '< 3 sec', label: 'Quote Generation', icon: Clock },
                { value: '24/7', label: 'Lead Capture', icon: Globe },
                { value: '0', label: 'Third-Party Tools Needed', icon: Code2 },
                { value: '∞', label: 'Estimates Included Free', icon: Calculator },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center"
                >
                  <stat.icon className="size-6 text-emerald-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white font-mono mb-1">{stat.value}</div>
                  <div className="text-white/70 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* CTA */}
          <FeatureSignup
            title="Turn Every Visitor Into a Qualified Lead"
            buttonLabel="Get Your Free Widget"
          >
            Your competitors are still waiting for phone calls. Your website should be closing while
            you sleep.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default CanvassingWidgetPage;
